import axios from 'axios'

export const DESKTOP_APP_ROUTE = '/api/rest/cas/callapps'
export const EVENT_ROUTE = '/api/rest/cas/callevent'

const getDesktopActivity = async (ccrid, isRootRecording = false) => {
  try {
    const response = await axios.get(DESKTOP_APP_ROUTE, { params: { ccrid, isRootRecording } })
    return formatDesktopAppUse(response.data)
  } catch (error) {
    throw new Error(error)
  }
}

const getDesktopEvents = async (ccrid, isRootRecording = false) => {
  try {
    const response = await axios.get(EVENT_ROUTE, { params: { ccrid, isRootRecording } })
    return formatDesktopEventData(response.data, true)
  } catch (error) {
    throw new Error(error)
  }
}

const parseDocumentLabel = documentLabel => (documentLabel ? documentLabel.split('<br>') : [])
const parseAppUse = app => ({
  start: getAppStart(app),
  name: app.application,
  notApproved: app.notApproved,
  duration: getAppDuration(app),
  hasDocument: !!app.document,
  documentLabel: parseDocumentLabel(app.documentLabel),
})

/**
 * used for creating meaningful JSON from API responses that look like the stuff in the mocks director
 */
const formatDesktopEventData = (resp, event) => {
  //Map of the column names and indicies
  let model
  const respJSON = []

  //Get the header
  resp.some(item => {
    // Make a map of the column names and their indicies and store this in the model
    if (item.header) {
      model = item.header.reduce((accumulatedHeader, current, idx) => {
        accumulatedHeader[current.col] = idx
        return accumulatedHeader
      }, {})
    }
    return model
  })

  //Get the items
  resp.forEach(item => {
    //If the item has data
    if (item.data) {
      //For each header item (from the model) store the relevant data for each datum
      const datum = Object.keys(model).reduce((accumulatedDatum, modelKey) => {
        accumulatedDatum[modelKey] = item.data[model[modelKey]]
        return accumulatedDatum
      }, {})

      //If we are getting event data, force the data type to be event
      if (event) {
        datum.type = 'EVENT'
      }

      respJSON.push(datum)
    }
  })
  return respJSON
}

const getAppStart = app =>
  app.applicationInfocusStart ? app.applicationInfocusStart : app['application infocus start']
const getAppDuration = app =>
  app.applicationInfocusDuration ? app.applicationInfocusDuration : app['application infocus duration']

const getInteractionStart = app => app.start
const getInteractionDuration = app => (app.duration ? app.duration : app.applicationDuration)

const formatDesktopAppUse = appUse => {
  const formattedData = formatDesktopEventData(appUse)

  //This algorithm works because the applications are sorted by when they start
  return formattedData
    .sort((a, b) => a.applicationInfocusStart - b.applicationInfocusStart)
    .reduce(
      (acc, curr) => {
        const lastInteractionStateIndex = acc.interactionStates.length - 1
        const lastAppUseIndex = acc.appUse.length - 1

        //If first interaction push entry to app and interaction states
        if (lastInteractionStateIndex === -1) {
          acc.interactionStates.push({
            type: curr.callState,
            start: getInteractionStart(curr),
            duration: getInteractionDuration(curr),
          })
          acc.appUse.push(parseAppUse(curr))
        } else {
          // If last interaction state isn't the current state
          if (acc.interactionStates[lastInteractionStateIndex].type !== curr.callState) {
            //Last interaction state's duration will be from the point that it starts, to the point that the current app becomes focused
            acc.interactionStates[lastInteractionStateIndex].duration =
              getInteractionStart(curr) - acc.interactionStates[lastInteractionStateIndex].start

            //Push current state to the interaction states
            acc.interactionStates.push({
              type: curr.callState,
              start: getInteractionStart(curr),
              duration: getInteractionDuration(curr),
            })

            // If last interaction state is the current state, with a different start time,
            // add the current application focused duration to the interaction state's duration
          } else if (acc.interactionStates[lastInteractionStateIndex].start !== getInteractionStart(curr)) {
            acc.interactionStates[lastInteractionStateIndex].duration += getInteractionDuration(curr)
          }

          //If the current application is not the same as the last one used
          if (acc.appUse[lastAppUseIndex].name !== curr.application) {
            //Last app's duration will be from the point that it starts, to the point that the current app starts
            acc.appUse[lastAppUseIndex].duration = getAppStart(curr) - acc.appUse[lastAppUseIndex].start

            //Push the current app
            acc.appUse.push(parseAppUse(curr))

            // If last app is the same as the current app, with a different start time,
            // add the current application focused duration to the apps duration
          } else if (acc.appUse[lastAppUseIndex].start !== getAppStart(curr)) {
            acc.appUse[lastAppUseIndex].duration += getAppDuration(curr)
          }
        }

        return acc
      },
      {
        interactionStates: [],
        appUse: [],
      },
    )
}

export default {
  formatDesktopEventData,
  formatDesktopAppUse,
  getDesktopActivity,
  getDesktopEvents,
}
